import type { Schema } from "@baton/api";
import { movedRoute } from "@baton/core";
import { useI18n } from "@baton/i18n";
import { Button } from "@baton/ui";
import { useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useRouteMeta } from "../routes/meta.ts";

/**
 * 001 FR-157, `contracts/screens.md` § Error and utility screens (2026-09-29): the page a signed-in
 * member sees at an old address of one of their own Teams. The old address redirects nowhere, by
 * the owner's rule, so the page says what happened and links to the same screen at the new
 * address. The server answers the lookup from the person's own memberships alone, so a person who
 * is not in the Team sees the no-access panel instead and learns nothing about it.
 */
export function TeamMoved({ former }: { readonly former: Schema<"FormerAddress"> }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const heading = useRef<HTMLHeadingElement>(null);
  useRouteMeta({ screen: "teamMoved.title", focus: heading });
  const target = movedRoute(`${location.pathname}${location.search}${location.hash}`, former.slug, former.workspace.slug);
  return (
    <div className="b-no-access" role="alert">
      <h1 ref={heading} tabIndex={-1} className="b-no-access__title">
        {t("teamMoved.title")}
      </h1>
      <p className="b-no-access__body">{t("teamMoved.body", { name: former.workspace.name, old: `${window.location.host}/${former.slug}`, address: `${window.location.host}/${former.workspace.slug}` })}</p>
      <div className="b-no-access__actions">
        <Button
          variant="primary"
          onClick={() => {
            void navigate(target, { replace: true });
          }}
        >
          {t("teamMoved.go", { name: former.workspace.name })}
        </Button>
      </div>
    </div>
  );
}
